/**
 * Seletor de template do WhatsApp Oficial (#28). Fora da janela de 24 horas da última mensagem
 * do contato a Meta só aceita template aprovado: o operador escolhe um, preenche as variáveis
 * e envia. A lista vem da WABA (só os APPROVED servem).
 */

import { useEffect, useMemo, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { listWhatsappMetaTemplates } from '../../db/api.js'
import {
  approvedTemplates, buildWaTemplate, renderTemplateBody, templateReady, templateVariableCount,
} from '../../lib/waTemplate.js'

export default function WhatsappTemplatePicker({ accountId, onClose, onSend }) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [picked, setPicked] = useState('')
  const [values, setValues] = useState([])
  const [sending, setSending] = useState(false)

  useEffect(() => {
    let alive = true
    listWhatsappMetaTemplates(accountId)
      .then((rows) => { if (alive) setTemplates(approvedTemplates(rows)) })
      .catch((e) => { if (alive) setErr(e.message || 'Falha ao carregar os templates.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [accountId])

  const tpl = useMemo(
    () => templates.find((t) => `${t.name}:${t.language}` === picked) || null,
    [templates, picked],
  )
  const count = tpl ? templateVariableCount(tpl) : 0
  const ready = !!tpl && templateReady(tpl, values)

  const choose = (key) => { setPicked(key); setValues([]) }
  const setValue = (i, v) => setValues((cur) => { const next = [...cur]; next[i] = v; return next })

  const submit = async () => {
    if (!ready || sending) return
    setSending(true)
    try {
      await onSend(buildWaTemplate(tpl, values), renderTemplateBody(tpl, values))
      onClose()
    } catch { /* o chamador já avisou o erro; o modal fica aberto para tentar de novo */ }
    finally { setSending(false) }
  }

  return (
    <div className="at-modal-overlay" onClick={onClose}>
      <div className="at-modal at-modal-form" onClick={(e) => e.stopPropagation()}>
        <div className="at-modal-head">
          <h3>Enviar template</h3>
          <button className="at-icon-btn" onClick={onClose} aria-label="Fechar"><Icon n="close" s={16} /></button>
        </div>

        <div className="at-form">
        {loading && <div className="at-muted">Carregando templates…</div>}
        {err && <div className="at-error">{err}</div>}
        {!loading && !err && templates.length === 0 && (
          <div className="at-muted">
            Nenhum template aprovado nesta conta. Crie e aprove um no Gerenciador do WhatsApp da Meta.
          </div>
        )}

        {templates.length > 0 && (
          <label className="at-field">
            <span>Template</span>
            <select value={picked} onChange={(e) => choose(e.target.value)}>
              <option value="">Escolha um template aprovado</option>
              {templates.map((t) => (
                <option key={`${t.name}:${t.language}`} value={`${t.name}:${t.language}`}>
                  {t.name} ({t.language})
                </option>
              ))}
            </select>
          </label>
        )}

        {tpl && Array.from({ length: count }, (_, i) => (
          <label className="at-field" key={i}>
            <span>Variável {`{{${i + 1}}}`}</span>
            <input value={values[i] || ''} onChange={(e) => setValue(i, e.target.value)} />
          </label>
        ))}

        {tpl && <div className="at-template-preview">{renderTemplateBody(tpl, values)}</div>}
        </div>

        <div className="at-modal-foot">
          <button className="at-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="at-btn-primary" onClick={submit} disabled={!ready || sending}>
            {sending ? 'Enviando…' : 'Enviar template'}
          </button>
        </div>
      </div>
    </div>
  )
}
